import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { apiRequest } from '../services/apiClient';
import { clockIn, clockOut, endBreak, endLunch, getRecentTimeEntries, getTimekeepingStatus, startBreak, startLunch } from '../services/timekeepingApi';
import './TimeClockPage.css';

const ERROR_COPY = {
  EMPLOYEE_MAPPING_REQUIRED: 'Your employee profile is not connected to this account.',
  EMPLOYEE_INACTIVE: 'Timekeeping is unavailable for your employee profile.',
  LOCATION_REQUIRED: 'Select a work location before clocking in.',
  LOCATION_NOT_AUTHORIZED: 'You are not authorized to clock in at this location.',
  ALREADY_CLOCKED_IN: "You're already clocked in.",
  NOT_CLOCKED_IN: "You're already clocked out.",
  INTERVAL_OPEN: 'End your current break or lunch before clocking out.',
  INTERVAL_ALREADY_OPEN: 'End your current break or lunch first.',
  NO_OPEN_BREAK: 'No break is currently open.',
  NO_OPEN_LUNCH: 'No lunch is currently open.'
};

function errorCode(error) {
  return error?.payload?.code || '';
}

function formatTime(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(new Date(value));
}

function formatDate(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(value));
}

function elapsedLabel(clockInAt, now) {
  const seconds = Math.max(0, Math.floor((now - new Date(clockInAt).getTime()) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return [hours, minutes, remainder].map(value => String(value).padStart(2, '0')).join(':');
}

function durationLabel(entry) {
  if (!entry.clockOutAt || entry.status !== 'completed') return 'In progress';
  const minutes = Number.isFinite(entry.durationMinutes)
    ? entry.durationMinutes
    : Math.max(0, Math.floor((new Date(entry.clockOutAt) - new Date(entry.clockInAt)) / 60000));
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export default function TimeClockPage() {
  const { logout, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [entries, setEntries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const errorRef = useRef(null);

  const loadStatus = useCallback(async () => {
    const next = await getTimekeepingStatus();
    setStatus(next);
    return next;
  }, []);

  const loadEntries = useCallback(async () => {
    const payload = await getRecentTimeEntries();
    setEntries(Array.isArray(payload?.items) ? payload.items : []);
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([getTimekeepingStatus(), getRecentTimeEntries(), apiRequest('/api/locations')])
      .then(([nextStatus, history, locationRows]) => {
        if (!active) return;
        const assigned = new Set([
          user?.locationId,
          ...(Array.isArray(user?.assignedLocationIds) ? user.assignedLocationIds : [])
        ].filter(Boolean).map(Number));
        const safeLocations = (Array.isArray(locationRows) ? locationRows : [])
          .filter(location => location.isActive !== false && assigned.has(Number(location.id)));
        setStatus(nextStatus);
        setEntries(Array.isArray(history?.items) ? history.items : []);
        setLocations(safeLocations);
        if (safeLocations.length) setSelectedLocationId(String(safeLocations[0].id));
      })
      .catch(err => {
        if (!active) return;
        setError(ERROR_COPY[errorCode(err)] || 'Time clock is unavailable. Check your connection and try again.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [user?.locationId, user?.assignedLocationIds]);

  useEffect(() => {
    if (status?.state === 'NOT_CLOCKED_IN' || !status?.entry?.clockInAt) return undefined;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [status?.state, status?.entry?.clockInAt]);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  const activeLocation = useMemo(() => {
    if (status?.entry?.location?.name) return status.entry.location;
    return locations.find(location => String(location.id) === String(selectedLocationId)) || null;
  }, [locations, selectedLocationId, status]);

  const resolveAmbiguousFailure = useCallback(async (err) => {
    const code = errorCode(err);
    if (code === 'ALREADY_CLOCKED_IN' || code === 'NOT_CLOCKED_IN' || !code || err?.status === 408) {
      try { await loadStatus(); } catch (_) { /* keep safe employee copy below */ }
    }
    setError(ERROR_COPY[code] || (navigator.onLine === false
      ? 'A network connection is required to use the time clock. No punch was saved on this device.'
      : 'We could not confirm that punch. We checked the current server status; review it before trying again.'));
  }, [loadStatus]);

  const handlePunch = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      if (status?.state === 'CLOCKED_IN') {
        const result = await clockOut();
        setStatus({ state: result.state });
        setMessage('Clock-out recorded.');
        await loadEntries();
      } else {
        const result = await clockIn(selectedLocationId);
        setStatus(result);
        setMessage('Clock-in recorded.');
      }
    } catch (err) {
      await resolveAmbiguousFailure(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInterval = async (action, success) => {
    if (submitting) return;
    setSubmitting(true); setError(''); setMessage('');
    try { const result = await action(); setStatus(result); setMessage(success); await loadEntries(); }
    catch (err) { await resolveAmbiguousFailure(err); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return <main className="time-clock-page" aria-busy="true"><p role="status">Loading your time clock…</p></main>;
  }

  const clockedIn = status?.state !== 'NOT_CLOCKED_IN';
  const working = status?.state === 'CLOCKED_IN';
  const statusLabel = status?.state === 'ON_BREAK' ? 'On Break' : status?.state === 'ON_LUNCH' ? 'At Lunch' : clockedIn ? 'Clocked In' : 'Not Clocked In';
  return (
    <div className="time-clock-shell">
      <header className="time-clock-header">
        <span className="time-clock-brand">VizionOps</span>
        <Button variant="ghost" size="sm" onClick={logout}>Sign out</Button>
      </header>
      <main className="time-clock-page">
        <div className="time-clock-title"><h1>Time Clock</h1><p>Record your work time from an authorized location.</p></div>
        <div className="time-clock-live" aria-live="polite" aria-atomic="true">
          {message && <p className="time-clock-message time-clock-message--success">{message}</p>}
          {error && <p className="time-clock-message time-clock-message--error" ref={errorRef} tabIndex="-1">{error}</p>}
        </div>
        <Card className="time-clock-status-card">
          <p className="time-clock-eyebrow">Current status</p>
          <h2>{statusLabel}</h2>
          {clockedIn && <p className="time-clock-elapsed" aria-label={`Elapsed time ${elapsedLabel(status.entry.clockInAt, now)}`}>{elapsedLabel(status.entry.clockInAt, now)}</p>}
          <dl className="time-clock-details">
            {clockedIn && <><dt>Started</dt><dd>{formatTime(status.entry.clockInAt)}</dd></>}
            {clockedIn && <><dt>Schedule</dt><dd>{status.entry.associationState === 'AMBIGUOUS_MATCH' ? 'Shift link under review' : status.entry.scheduledShift ? 'Scheduled shift' : 'No shift linked'}</dd></>}
            <dt>Location</dt><dd>{activeLocation?.name || 'No authorized location available'}</dd>
            <dt>Time display</dt><dd>Your device time</dd>
          </dl>
          {!clockedIn && locations.length > 1 && (
            <label className="time-clock-location-label" htmlFor="punch-location">Work location
              <select id="punch-location" value={selectedLocationId} onChange={event => setSelectedLocationId(event.target.value)}>
                {locations.map(location => <option key={location.id} value={location.id}>{location.name || location.code}</option>)}
              </select>
            </label>
          )}
          <div className="time-clock-actions">
            {!clockedIn && <Button size="lg" fullWidth loading={submitting} loadingLabel="Clocking in…" disabled={!selectedLocationId} onClick={handlePunch}>Clock In</Button>}
            {working && <><Button size="lg" fullWidth loading={submitting} onClick={() => handleInterval(startBreak, 'Break started.')}>Start Break</Button><Button size="lg" fullWidth loading={submitting} onClick={() => handleInterval(startLunch, 'Lunch started.')}>Start Lunch</Button><Button size="lg" fullWidth loading={submitting} loadingLabel="Clocking out…" onClick={handlePunch}>Clock Out</Button></>}
            {status?.state === 'ON_BREAK' && <Button size="lg" fullWidth loading={submitting} onClick={() => handleInterval(endBreak, 'Break ended.')}>End Break</Button>}
            {status?.state === 'ON_LUNCH' && <Button size="lg" fullWidth loading={submitting} onClick={() => handleInterval(endLunch, 'Lunch ended.')}>End Lunch</Button>}
          </div>
        </Card>
        <section className="time-clock-history" aria-labelledby="recent-entries-heading">
          <h2 id="recent-entries-heading">Recent entries</h2>
          {entries.length === 0 ? <p>No time entries yet.</p> : (
            <div className="time-clock-entry-list">
              {entries.map(entry => (
                <article className="time-clock-entry" key={entry.id}>
                  <div><strong>{formatDate(entry.clockInAt)}</strong><span>{entry.location?.name || 'Location unavailable'}</span></div>
                  <dl><dt>In</dt><dd>{formatTime(entry.clockInAt)}</dd><dt>Out</dt><dd>{entry.clockOutAt ? formatTime(entry.clockOutAt) : 'Still clocked in'}</dd><dt>Duration</dt><dd>{durationLabel(entry)}</dd><dt>Breaks</dt><dd>{entry.breakCount || 0} · {entry.breakMinutes || 0} min</dd><dt>Lunch</dt><dd>{entry.lunchCount || 0} · {entry.lunchMinutes || 0} min</dd><dt>Schedule</dt><dd>{entry.associationState === 'AMBIGUOUS_MATCH' ? 'Shift link under review' : entry.scheduledShift ? 'Scheduled shift' : 'No shift linked'}</dd><dt>Approval</dt><dd>{entry.timecardApproval?.status || 'OPEN'}{entry.timecardApproval?.version ? ` · v${entry.timecardApproval.version}` : ''}</dd></dl>
                  {(entry.intervals || []).length > 0 && <ul className="time-clock-intervals">{entry.intervals.map(interval => <li key={interval.id}>{interval.type === 'BREAK' ? 'Break' : 'Lunch'}: {formatTime(interval.startsAt)}–{interval.endsAt ? formatTime(interval.endsAt) : 'In progress'}</li>)}</ul>}
                </article>
              ))}
            </div>
          )}
          <p className="time-clock-zone-note">All clock times above are shown in your device time zone.</p>
        </section>
      </main>
    </div>
  );
}

export { durationLabel, elapsedLabel };
